import { alpha, keyframes, type SxProps, type Theme } from "@mui/material/styles";

import { customGradients, customShadows } from "@/theme/customTokens";

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const pop = keyframes`
  0% { transform: scale(0.6); opacity: 0; }
  70% { transform: scale(1.06); opacity: 1; }
  100% { transform: scale(1); }
`;

export const root: SxProps<Theme> = {
  minHeight: "100dvh",
  display: "flex",
  flexDirection: "column",
  bgcolor: "background.default",
  color: "text.primary",
};

export const header: SxProps<Theme> = (theme) => ({
  height: 64,
  flex: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  bgcolor: "background.paper",
  borderBottom: 1,
  borderColor: "divider",
  boxShadow: theme.shadows[1],
});

export const headerLogo: SxProps<Theme> = {
  height: 26,
  width: "auto",
  objectFit: "contain",
};

export const main: SxProps<Theme> = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  px: { xs: 2, sm: 3 },
  py: { xs: 4, sm: 6 },
};

export const card: SxProps<Theme> = (theme) => ({
  position: "relative",
  overflow: "hidden",
  width: "100%",
  maxWidth: 520,
  bgcolor: "background.paper",
  border: 1,
  borderColor: "divider",
  borderRadius: 3,
  boxShadow: theme.shadows[3],
  px: { xs: 2.5, sm: 4 },
  pt: { xs: 6, sm: 7 },
  pb: { xs: 4, sm: 5 },
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(1.5),
  textAlign: "center",
  "&::before": {
    content: '""',
    position: "absolute",
    insetInline: 0,
    top: 0,
    height: 6,
    background: customGradients.instagram,
  },
});

export const pair: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  mb: 1,
});

export const brandTile: SxProps<Theme> = {
  width: 64,
  height: 64,
  borderRadius: 2.5,
  bgcolor: "background.default",
  border: 1,
  borderColor: "divider",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export const brandLogo: SxProps<Theme> = {
  width: 40,
  height: 40,
  objectFit: "contain",
};

export const instagramTile: SxProps<Theme> = {
  width: 64,
  height: 64,
  borderRadius: 2.5,
  background: customGradients.instagram,
  boxShadow: customShadows.instagram,
  color: "common.white",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export const link =
  (tone: "success" | "error" | "neutral"): SxProps<Theme> =>
  (theme) => ({
    width: 34,
    height: 34,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bgcolor:
      tone === "neutral"
        ? alpha(theme.palette.primary.main, 0.08)
        : alpha(theme.palette[tone].main, 0.12),
    animation: tone === "neutral" ? "none" : `${pop} 0.5s ease both`,
  });

export const spinner: SxProps<Theme> = (theme) => ({
  width: 18,
  height: 18,
  borderRadius: "50%",
  border: `3px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  borderTopColor: theme.palette.primary.main,
  animation: `${spin} 0.8s linear infinite`,
});

export const title: SxProps<Theme> = {
  fontWeight: 800,
  fontSize: { xs: "1.125rem", sm: "1.3125rem" },
};

export const description: SxProps<Theme> = {
  fontSize: "0.84375rem",
  color: "text.secondary",
  lineHeight: 1.9,
  maxWidth: 400,
};

export const actions: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: { xs: "column", sm: "row" },
  gap: theme.spacing(1.25),
  mt: 1.5,
  width: { xs: "100%", sm: "auto" },
});

export const actionButton: SxProps<Theme> = {
  minWidth: 170,
  minHeight: 46,
};

export const outlinedButton: SxProps<Theme> = {
  minWidth: 170,
  minHeight: 46,
  borderRadius: 1.5,
  fontWeight: 700,
};
